import { useLayoutEffect, useRef } from 'react';

/**
 * Shrinks an element's own font-size (within [minFontSize, maxFontSize]) until
 * its content fits inside maxHeight. Designed for translated UI copy: a
 * translation can render far longer than the English original at the same
 * font-size, wrapping into extra lines and inflating the surrounding layout
 * (headline/rail height, card height, etc). Rather than hardcoding per-language
 * exceptions, this measures the actual rendered content and scales it down —
 * so any future language gets the same treatment automatically.
 *
 * Re-runs whenever the text content or sizing options change (e.g. a language
 * switch) or the element is resized (container width changes wrap points).
 */
export default function useFitText({ maxHeight, minFontSize, maxFontSize, deps = [] }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !maxHeight || !maxFontSize) return;

    const fit = () => {
      let size = maxFontSize;
      el.style.fontSize = `${size}px`;
      while (el.scrollHeight > maxHeight && size > minFontSize) {
        size -= 1;
        el.style.fontSize = `${size}px`;
      }
    };

    fit();

    // Re-fit on viewport/container width changes (e.g. window resize) — but
    // not via ResizeObserver on `el` itself, since our own font-size writes
    // would re-trigger it. Watch the parent instead, which we don't mutate.
    const target = el.parentElement || el;
    const observer = new ResizeObserver(fit);
    observer.observe(target);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxHeight, minFontSize, maxFontSize, ...deps]);

  return ref;
}
